import React, { useState, useEffect, useCallback } from 'react';
import './DateSelectionBox.css';
import PricingEngine from '../services/PricingEngine';
import currencyService from '../services/currencyService';

// Move these constants outside the component - currencies will be loaded dynamically
const DEFAULT_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'KRW', symbol: '₩', name: 'Korean Won' },
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: '$', name: 'Australian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'ALL', symbol: 'L', name: 'Albanian Lek' },
];

function DateSelectionBox({ 
  selectedRoute,
  onBookNow,
  onClose 
}) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [dailyAllocations, setDailyAllocations] = useState([]);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [availableCurrencies, setAvailableCurrencies] = useState(DEFAULT_CURRENCIES);
  const [isPriceLoading, setIsPriceLoading] = useState(false);

  // Load available currencies from backend
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const backendCurrencies = await currencyService.getAvailableCurrencies();
        // Map currency codes to full currency objects
        const currencyObjects = backendCurrencies.map(code => 
          DEFAULT_CURRENCIES.find(c => c.code === code) || 
          { code, symbol: code, name: code }
        );
        setAvailableCurrencies(currencyObjects);
      } catch (error) {
        console.error('Error loading currencies:', error);
        // Keep default currencies if backend fails
      }
    };
    loadCurrencies();
  }, []);

  // Close currency dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCurrencyDropdown && !event.target.closest('.currency-dropdown')) {
        setShowCurrencyDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showCurrencyDropdown]);

  const getNights = useCallback(() => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate nights correctly: the difference in days
    // For example: Check-in 19th, Check-out 26th = 7 nights (19, 20, 21, 22, 23, 24, 25)
    const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return nights;
  }, [startDate, endDate]);

  // Wrap getDateValidationStatus in useCallback
  const getDateValidationStatus = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check start date first (even if end date is not selected)
    if (startDate) {
      const start = new Date(startDate);
      
      if (start < today) {
        return 'pastDate';
      }
      
      // Check if start date is more than a year in advance
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
      if (start > oneYearFromNow) {
        return 'tooFarFuture';
      }
    }
    
    // Only check the rest if both dates are selected
    if (!startDate || !endDate) return null;
    
    const end = new Date(endDate);
    const nights = getNights();
    
    // Check end date
    if (end < today) {
      return 'pastDate';
    }
    
    // Check if end date is more than a year in advance
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    if (end > oneYearFromNow) {
      return 'tooFarFuture';
    }
    
    if (nights > 30) {
      return 'tooManyNights';
    }
    
    if (nights < 2) {
      return 'tooFewNights';
    }
    
    return 'valid';
  }, [startDate, endDate, getNights]);

  // Wrap calculatePrice in useCallback to avoid dependency issues
  const calculatePrice = useCallback(async () => {
    if (!startDate || !endDate || !selectedRoute) return 0;

    const nights = getNights();

    if (nights <= 0 || nights < 2 || nights > 30 || getDateValidationStatus() !== 'valid') return 0;
    
    try {
      // Use local pricing engine for now (can switch to API later)
      const pricingResult = await PricingEngine.calculatePassPrice(
        selectedRoute,
        startDate,
        endDate,
        selectedRoute.pricingSettings
      );
      
      // Store allocations for later use
      setDailyAllocations(pricingResult.dailyAllocations);
      
      // Convert to selected currency (pricing engine returns USD)
      const priceInUSD = pricingResult.price;
      const conversion = await currencyService.convertFromUSD(priceInUSD, selectedCurrency);
      return Math.round(conversion.amount);
      
    } catch (error) {
      console.error('Pricing calculation error:', error);
      // Fallback to simple calculation
      const basePrice = selectedRoute.storePrice || 599;
      // const pricePerNight = basePrice / 5;
      // const priceInEuro = pricePerNight * nights;
      const conversion = await currencyService.convertFromUSD(basePrice, selectedCurrency);
      return Math.round(conversion.amount);
    }
  }, [startDate, endDate, selectedRoute, selectedCurrency, getNights, getDateValidationStatus]);

  // Update the price display to be async
  useEffect(() => {
    const updatePrice = async () => {
      setIsPriceLoading(true);
      try {
        const newPrice = await calculatePrice();
        setCalculatedPrice(newPrice);
      } finally {
        setIsPriceLoading(false);
      }
    };
    
    if (startDate && endDate) {
      updatePrice();
    } else {
      setCalculatedPrice(0);
      setIsPriceLoading(false);
    }
  }, [startDate, endDate, selectedCurrency, selectedRoute, calculatePrice]);

  const handleCalendarClick = (inputId, e) => {
    e.stopPropagation();
    const input = document.getElementById(inputId);
    if (input) {
      // Try modern showPicker() method first, fallback to click()
      if (input.showPicker) {
        try {
          input.showPicker();
        } catch (error) {
          input.click();
        }
      } else {
        input.click();
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Split the date string to avoid timezone issues
    const [year, month, day] = dateString.split('-');
    
    // Format based on currency
    if (['CNY', 'JPY', 'KRW'].includes(selectedCurrency)) {
      // YMD format for Asian currencies
      return `${year}/${month}/${day}`;
    } else if (selectedCurrency === 'USD') {
      // MDY format for US Dollar
      return `${month}/${day}/${year}`;
    } else {
      // DMY format for everything else
      return `${day}/${month}/${year}`;
    }
  };

  const getCurrencySymbol = () => {
    return availableCurrencies.find(c => c.code === selectedCurrency)?.symbol || '$';
  };

  const handleCurrencySelect = (code) => {
    setSelectedCurrency(code);
    setShowCurrencyDropdown(false);
    // Clear price to force recalculation with new currency
    setCalculatedPrice(0);
    setIsPriceLoading(true);
  };

  const getSeparatorContent = () => {
    const validationStatus = getDateValidationStatus();
    
    // If start date is selected and has an error, show red X
    if (startDate && !endDate && (validationStatus === 'pastDate' || validationStatus === 'tooFarFuture')) {
      return (
        <svg width="32" height="26" viewBox="0 0 32 26" fill="none">
          <path d="M8 8L24 18M24 8L8 18" stroke="#FF4444" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      );
    }
    
    if (!startDate && !endDate) {
      // No dates selected - white curved line
      return (
        <svg width="70" height="26" viewBox="0 0 70 26" fill="none">
          <path 
            d="M 10 13 Q 35 8 60 13" 
            stroke="#FFFFFF" 
            strokeWidth="2" 
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      );
    } else if (startDate && endDate && validationStatus !== 'valid' && validationStatus !== null) {
      // Both dates selected with any validation error - red X
      return (
        <svg width="32" height="26" viewBox="0 0 32 26" fill="none">
          <path d="M8 8L24 18M24 8L8 18" stroke="#FF4444" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      );
    } else if (startDate && endDate && validationStatus === 'valid') {
      // Both dates selected and valid - yellow checkmark
      return (
        <svg width="32" height="26" viewBox="0 0 32 26" fill="none">
          <path d="M6 13L13 20L26 7" stroke="#FFD700" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    } else if (startDate && !endDate) {
      // Only start date selected - left half yellow, right half white
      return (
        <svg width="70" height="26" viewBox="0 0 70 26" fill="none">
          <defs>
            <linearGradient id="leftYellowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFD700" />
              <stop offset="50%" stopColor="#FFD700" />
              <stop offset="50%" stopColor="#FFFFFF" />
              <stop offset="100%" stopColor="#FFFFFF" />
            </linearGradient>
          </defs>
          <path 
            d="M 10 13 Q 35 8 60 13" 
            stroke="url(#leftYellowGradient)" 
            strokeWidth="2" 
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      );
    } else if (!startDate && endDate) {
      // Only end date selected - left half white, right half yellow
      return (
        <svg width="70" height="26" viewBox="0 0 70 26" fill="none">
          <defs>
            <linearGradient id="rightYellowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="50%" stopColor="#FFFFFF" />
              <stop offset="50%" stopColor="#FFD700" />
              <stop offset="100%" stopColor="#FFD700" />
            </linearGradient>
          </defs>
          <path 
            d="M 10 13 Q 35 8 60 13" 
            stroke="url(#rightYellowGradient)" 
            strokeWidth="2" 
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      );
    } else {
      // Fallback - white curved line
      return (
        <svg width="70" height="26" viewBox="0 0 70 26" fill="none">
          <path 
            d="M 10 13 Q 35 8 60 13" 
            stroke="#FFFFFF" 
            strokeWidth="2" 
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      );
    }
  };

  const getDateStatusMessage = () => {
    const validationStatus = getDateValidationStatus();
    const nights = getNights();
    
    // Show error even if only start date is selected
    if (validationStatus === 'pastDate') {
      return <div className="nights-text error">You Cannot Start in the Past</div>;
    }
    
    if (validationStatus === 'tooFarFuture') {
      return <div className="nights-text error">Too Far in the Future</div>;
    }
    
    // Only show these if both dates are selected
    if (startDate && endDate) {
      switch(validationStatus) {
        case 'tooManyNights':
          return <div className="nights-text error">Select Up to 30 Days</div>;
        case 'tooFewNights':
          return <div className="nights-text error">Select At Least 2 Nights</div>;
        case 'valid':
          return <div className="nights-text"><em>For {nights} Nights</em></div>;
        default:
          return null;
      }
    }
    
    return null;
  };

  return (
    <div className="date-selection-box">
      {/* Currency Selector in top left */}
      <div className="currency-selector-container">
        <div className="currency-dropdown">
          <button 
            className="currency-dropdown-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowCurrencyDropdown(!showCurrencyDropdown);
            }}
          >
            <span className="currency-icon">💱</span>
            <span>{selectedCurrency}</span>
            <span className="currency-symbol">{getCurrencySymbol()}</span>
          </button>
          {showCurrencyDropdown && (
            <div className="currency-dropdown-menu">
              {availableCurrencies.map(currency => (
                <button
                  key={currency.code}
                  className={`currency-option ${selectedCurrency === currency.code ? 'selected' : ''}`}
                  onClick={() => handleCurrencySelect(currency.code)}
                >
                  <span>{currency.code}</span>
                  <span>{currency.symbol}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* REMOVED CLOSE BUTTON */}
      
      <div className="date-header-group">
        <h3>{selectedRoute.name}</h3>
        <p className="popup-tagline">{selectedRoute.storeTagline}</p>
      </div>
      
      {/* Date Selection - Simplified */}
      <div className="date-selection-wrapper">
        <div className="date-inputs-simple">
          <div className="date-box-container">
            <div 
              className="date-input-wrapper" 
              onClick={(e) => handleCalendarClick('start-date', e)}
            >
              <input 
                type="date" 
                id="start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input visually-hidden"
                tabIndex={0}
              />
              <div className={`calendar-icon ${startDate ? (getDateValidationStatus() !== 'valid' && getDateValidationStatus() !== null ? 'error' : 'selected') : ''}`} 
                   role="button" 
                   aria-label="Select start date">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
                </svg>
              </div>
            </div>
            <div className={`individual-date ${!startDate ? 'empty' : ''} ${getDateValidationStatus() !== 'valid' && getDateValidationStatus() !== null ? 'error' : ''}`}>
              {startDate ? formatDate(startDate) : ''}
            </div>
          </div>
          
          <div className="date-separator">
            {getSeparatorContent()}
          </div>
          
          <div className="date-box-container">
            <div 
              className="date-input-wrapper" 
              onClick={(e) => handleCalendarClick('end-date', e)}
            >
              <input 
                type="date" 
                id="end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
                className="date-input visually-hidden"
                tabIndex={0}
              />
              <div className={`calendar-icon ${endDate ? (getDateValidationStatus() !== 'valid' && getDateValidationStatus() !== null ? 'error' : 'selected') : ''}`} 
                   role="button" 
                   aria-label="Select end date">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
                </svg>
              </div>
            </div>
            <div className={`individual-date ${!endDate ? 'empty' : ''} ${getDateValidationStatus() !== 'valid' && getDateValidationStatus() !== null ? 'error' : ''}`}>
              {endDate ? formatDate(endDate) : ''}
            </div>
          </div>
        </div>
      </div>

      <div className="button-container">
        <button 
          className={`popup-cta ${calculatedPrice === 0 && !isPriceLoading ? 'when-mode' : ''}`}
          onClick={() => {
            onBookNow({
              price: calculatedPrice,
              currency: selectedCurrency,
              currencySymbol: getCurrencySymbol(),
              route: selectedRoute,
              startDate: startDate,
              endDate: endDate,
              nights: getNights(),
              dailyAllocations: dailyAllocations // Include allocations
            });
          }}
          disabled={!startDate || !endDate || calculatedPrice === 0 || isPriceLoading}
        >
          {isPriceLoading ? 'Calculating...' : calculatedPrice > 0 ? `${getCurrencySymbol()}${calculatedPrice}` : 'When?'}
        </button>
      </div>
      
      <div className="date-status-below">
        {(startDate || endDate) && getDateStatusMessage()}
      </div>
    </div>
  );
}

export default DateSelectionBox;